import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Modal,
  notification,
  Form,
  Input,
  message,
} from "lib/ui";
import { PlusOutlined, DeleteOutlined, SearchOutlined } from "lib/icons";
import {
  Image,
  Pencil,
  Trash2,
  Layers,
  Hash,
} from "lucide-react";
import AddBannerForm from "./Add";
import ApiPath from "../../../hooks/ApiFetch";

function bannerImageUrl(path) {
  if (!path) return null;
  if (String(path).startsWith("http")) return path;
  const base =
    process.env.REACT_APP_ASSET_URL?.trim() || "https://portal.tidyhub.ae";
  return `${base.replace(/\/+$/, "")}/${String(path).replace(/^\//, "")}`;
}

const BannerPage = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [form] = Form.useForm();
  const [searchName, setSearchName] = useState("");
  const [data, setData] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await ApiPath.get("/banners");
      setData(Array.isArray(response.data) ? response.data : []);
    } catch {
      message.error("Failed to load banners");
    }
  };

  const handleAdd = () => {
    setEditingBanner(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    form.setFieldsValue(banner);
    setIsModalVisible(true);
  };

  const handleDelete = (id, title) => {
    Modal.confirm({
      title: "Delete this banner?",
      icon: <DeleteOutlined />,
      content: title ? `"${title}" will be removed permanently.` : undefined,
      okText: "Delete",
      cancelText: "Cancel",
      okType: "danger",
      onOk: async () => {
        try {
          await ApiPath.delete(`/banners/${id}`);
          setData((prev) => prev.filter((item) => item._id !== id));
          notification.success({
            message: "Banner deleted",
            description: "The banner was removed.",
          });
        } catch {
          notification.error({
            message: "Delete failed",
            description: "Could not delete the banner.",
          });
        }
      },
    });
  };

  const handleSave = async (values) => {
    try {
      if (editingBanner) {
        await ApiPath.put(`/banners/${editingBanner._id}`, values);
        setData((prev) =>
          prev.map((item) =>
            item._id === editingBanner._id ? { ...item, ...values } : item
          )
        );
        notification.success({
          message: "Banner updated",
          description: "Changes saved successfully.",
        });
      } else {
        const response = await ApiPath.post("/banners", values);
        setData((prev) => [
          ...prev,
          { ...values, _id: response.data._id ?? response.data?._id },
        ]);
        notification.success({
          message: "Banner added",
          description: "New banner created.",
        });
      }
      setIsModalVisible(false);
      form.resetFields();
    } catch {
      notification.error({
        message: "Save failed",
        description: "Could not save the banner.",
      });
    }
  };

  const filteredData = useMemo(() => {
    const q = searchName.trim().toLowerCase();
    return [...data]
      .filter((item) => !q || (item.title ?? "").toLowerCase().includes(q))
      .sort(
        (a, b) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0)
      );
  }, [data, searchName]);

  const withImage = data.filter((b) => b.image).length;

  return (
    <div className="space-y-6 pb-10">
      <header className="relative overflow-hidden rounded-2xl border border-violet-200/60 bg-gradient-to-br from-brand-deeper via-brand-dark to-brand px-6 py-7 text-white shadow-[0_20px_50px_-12px_rgba(91,33,182,0.4)] md:px-8">
        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-violet-100 ring-1 ring-white/15">
              <Image size={14} strokeWidth={2.25} aria-hidden />
              Marketing
            </p>
            <h1 className="mt-3 text-2xl font-extrabold tracking-tight md:text-3xl">
              App banners
            </h1>
            <p className="mt-2 max-w-xl text-sm text-violet-100/90">
              Hero and promotional slides shown in the customer app — order,
              images, and metadata in one place.
            </p>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            className="!border-0 !bg-white !text-brand-dark hover:!bg-violet-50"
          >
            Add banner
          </Button>
        </div>
      </header>

      <div className="grid gap-4 sm:grid-cols-3">
        {[
          { label: "Total banners", value: data.length, icon: Layers },
          { label: "With image", value: withImage, icon: Image },
          { label: "Showing", value: filteredData.length, icon: Hash },
        ].map(({ label, value, icon: Icon }) => (
          <div
            key={label}
            className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-card"
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                {label}
              </p>
              <span className="rounded-lg bg-brand-surface p-2 text-brand">
                <Icon size={18} strokeWidth={2.25} />
              </span>
            </div>
            <p className="mt-2 text-2xl font-extrabold tabular-nums text-slate-900">
              {value}
            </p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-card">
        <Input
          placeholder="Search by title…"
          prefix={<SearchOutlined />}
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          className="w-full"
        />
      </div>

      {filteredData.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 py-16 text-center">
          <Image className="h-12 w-12 text-slate-300" strokeWidth={1.5} />
          <p className="mt-4 text-sm font-semibold text-slate-700">
            No banners found
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {searchName ? "Try a different search." : "Add your first banner."}
          </p>
          {!searchName ? (
            <Button
              type="primary"
              className="mt-4"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              Add banner
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {filteredData.map((banner) => {
            const src = bannerImageUrl(banner.image);
            const metaCount = (banner.metaFields || []).length;
            return (
              <article
                key={banner._id}
                className="group overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-card transition hover:-translate-y-0.5 hover:shadow-card-hover"
              >
                <div className="relative aspect-[16/9] overflow-hidden bg-gradient-to-br from-slate-100 to-brand-surface">
                  {src ? (
                    <img
                      src={src}
                      alt={banner.title}
                      className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="flex h-full flex-col items-center justify-center gap-2 text-slate-400">
                      <Image size={32} strokeWidth={1.5} />
                      <span className="text-xs font-medium">No image</span>
                    </div>
                  )}
                  <span className="absolute left-3 top-3 rounded-lg bg-white/95 px-2.5 py-1 text-xs font-bold tabular-nums text-brand-dark shadow-sm ring-1 ring-slate-200/80">
                    #{banner.sortOrder ?? "—"}
                  </span>
                </div>
                <div className="p-4">
                  <h3 className="line-clamp-2 text-base font-bold text-slate-900">
                    {banner.title}
                  </h3>
                  <p className="mt-1 text-xs text-slate-500">
                    {metaCount} meta field{metaCount === 1 ? "" : "s"}
                    {banner.createdAt
                      ? ` · ${new Date(banner.createdAt).toLocaleDateString()}`
                      : ""}
                  </p>
                  {metaCount > 0 ? (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {(banner.metaFields || []).slice(0, 3).map((m, i) => (
                        <span
                          key={i}
                          className="rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600"
                        >
                          {m.key}: {m.value}
                        </span>
                      ))}
                      {metaCount > 3 ? (
                        <span className="text-[10px] text-slate-400">
                          +{metaCount - 3} more
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="mt-4 flex gap-2 border-t border-slate-100 pt-4">
                    <button
                      type="button"
                      onClick={() => handleEdit(banner)}
                      className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-sky-50 py-2 text-xs font-semibold text-sky-800 transition hover:bg-sky-100"
                    >
                      <Pencil size={14} />
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(banner._id, banner.title)}
                      className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-red-50 py-2 text-xs font-semibold text-red-700 transition hover:bg-red-100"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Modal
        title={editingBanner ? "Edit banner" : "Add banner"}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
      >
        <AddBannerForm
          form={form}
          onFinish={handleSave}
          initialValues={editingBanner || {}}
        />
      </Modal>
    </div>
  );
};

export default BannerPage;
